import { Box, Grid, Skeleton, Tooltip, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import {
  CustomerStatMetric,
  GetCustomerBreakdownResponse,
} from "subModule/src/services/analytics/interface";
import { useGetCustomerSummaryService } from "subModule/src/services/analytics/useAnalytics";
import { dashboardWidgetIconSx } from "components/Analytics/dashboardWidgetLayout";

interface CustomerStatsWidgetsProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: {
    stationId: any;
    range: any;
    allStation: boolean;
    businessId?: any;
  };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
  stationsLoaded: boolean;
}

type WidgetConfig = {
  key: string;
  labelKey: string;
  valueKeys: string[];
  changeKeys?: string[];
  sparklineKeys?: string[];
  valueFormat: "count" | "percent" | "decimal";
  accent: string;
  bg: string;
  Icon: React.ElementType;
};

const WIDGETS: WidgetConfig[] = [
  {
    key: "activeCustomers",
    labelKey: "activeCustomers",
    valueKeys: ["activeCustomers", "servedCustomers"],
    changeKeys: ["activeCustomersChange", "activeCustomers"],
    sparklineKeys: ["activeCustomersSparkline", "activeCustomers"],
    valueFormat: "count",
    accent: "#7C3AED",
    bg: "#FFFFFF",
    Icon: DirectionsCarOutlinedIcon,
  },
  {
    key: "returningCustomers",
    labelKey: "returningCustomers",
    valueKeys: ["returningCustomers", "returnedCustomers"],
    changeKeys: [
      "returningCustomersChange",
      "returnedCustomersChange",
      "returningCustomers",
      "returnedCustomers",
    ],
    sparklineKeys: [
      "returningCustomersSparkline",
      "returnedCustomersSparkline",
      "returningCustomers",
      "returnedCustomers",
    ],
    valueFormat: "count",
    accent: "#2563EB",
    bg: "#FFFFFF",
    Icon: PersonOutlineOutlinedIcon,
  },
  {
    key: "newCustomers",
    labelKey: "newCustomers",
    valueKeys: ["newCustomers"],
    changeKeys: ["newCustomersChange", "newCustomers"],
    sparklineKeys: ["newCustomersSparkline", "newCustomers"],
    valueFormat: "count",
    accent: "#16A34A",
    bg: "#DCFCE7",
    Icon: PersonAddAltOutlinedIcon,
  },
  {
    key: "lostCustomers",
    labelKey: "lostCustomers",
    valueKeys: ["lostCustomers"],
    changeKeys: ["lostCustomersChange", "lostCustomers"],
    sparklineKeys: ["lostCustomersSparkline", "lostCustomers"],
    valueFormat: "count",
    accent: "#E11D48",
    bg: "#FFE4E6",
    Icon: GroupsOutlinedIcon,
  },
  {
    key: "retentionRate",
    labelKey: "retentionRate",
    valueKeys: ["retentionRate"],
    changeKeys: ["retentionRateChange", "retentionRate"],
    sparklineKeys: ["retentionRateSparkline", "retentionRate"],
    valueFormat: "percent",
    accent: "#0891B2",
    bg: "#CFFAFE",
    Icon: VerifiedUserOutlinedIcon,
  },
  {
    key: "avgVisitsPerCustomer",
    labelKey: "avgVisitsPerCustomer",
    valueKeys: ["avgVisitsPerCustomer", "avgVisits"],
    changeKeys: [
      "avgVisitsPerCustomerChange",
      "avgVisitsChange",
      "avgVisitsPerCustomer",
      "avgVisits",
    ],
    sparklineKeys: [
      "avgVisitsPerCustomerSparkline",
      "avgVisitsSparkline",
      "avgVisitsPerCustomer",
      "avgVisits",
    ],
    valueFormat: "decimal",
    accent: "#EA580C",
    bg: "#FFFFFF",
    Icon: InsightsOutlinedIcon,
  },
];

const emptyData = (): Record<string, any> => ({});

const asMetric = (
  raw: number | CustomerStatMetric | number[] | undefined,
): { value: number; change?: number; sparkline?: number[] } | null => {
  if (raw == null) return null;
  if (typeof raw === "number") return { value: raw };
  if (Array.isArray(raw)) return { value: 0, sparkline: raw.map(Number) };
  if (typeof raw === "object") {
    const value = Number(raw.value ?? 0);
    const change =
      raw.change ?? raw.comparisonPercentage ?? raw.comparison ?? undefined;
    const sparkline = raw.sparkline || raw.trend;
    return {
      value: Number.isFinite(value) ? value : 0,
      change:
        change == null || !Number.isFinite(Number(change))
          ? undefined
          : Number(change),
      sparkline: Array.isArray(sparkline)
        ? sparkline.map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : undefined,
    };
  }
  return null;
};

const resolveStat = (
  data: Record<string, any>,
  valueKeys: string[],
  changeKeys?: string[],
  sparklineKeys?: string[],
): { value: number; change?: number; sparkline?: number[] } => {
  let result: { value: number; change?: number; sparkline?: number[] } = {
    value: 0,
  };

  for (const key of valueKeys) {
    const metric = asMetric(data[key]);
    if (metric) {
      result = { ...result, value: metric.value, change: metric.change };
      if (metric.sparkline?.length) result.sparkline = metric.sparkline;
      break;
    }
  }

  if (result.change == null && changeKeys) {
    for (const changeKey of changeKeys) {
      const changeRaw = data[changeKey];
      if (typeof changeRaw === "number") {
        result.change = changeRaw;
        break;
      }
      const nested = asMetric(changeRaw);
      if (nested?.change != null) {
        result.change = nested.change;
        break;
      }
    }
  }

  if (!result.sparkline?.length && sparklineKeys) {
    for (const key of sparklineKeys) {
      const raw = data[key];
      if (Array.isArray(raw)) {
        result.sparkline = raw.map(Number).filter((n) => Number.isFinite(n));
        break;
      }
      const nested = asMetric(raw);
      if (nested?.sparkline?.length) {
        result.sparkline = nested.sparkline;
        break;
      }
    }
  }

  return result;
};

/** Decorative fallback sparkline when API has no series — shaped by change direction */
const buildFallbackSparkline = (seed: string, change?: number): number[] => {
  const rising = (change ?? 0) >= 0;
  const patterns: Record<string, number[]> = {
    activeCustomers: [8, 10, 9, 12, 11, 14, 13, 16, 18, 17, 20],
    returningCustomers: [6, 7, 6, 8, 9, 8, 10, 12, 11, 14, 18],
    newCustomers: [4, 5, 6, 5, 7, 8, 7, 9, 11, 12, 14],
    lostCustomers: [18, 16, 17, 15, 14, 16, 13, 12, 14, 11, 10],
    retentionRate: [10, 11, 10, 12, 13, 12, 14, 15, 14, 16, 17],
    avgVisitsPerCustomer: [9, 10, 9, 11, 10, 12, 11, 13, 12, 14, 15],
  };
  const base = patterns[seed] || patterns.activeCustomers;
  if (rising) return base;
  return [...base].reverse();
};

const Sparkline = ({ points, color }: { points: number[]; color: string }) => {
  const path = useMemo(() => {
    if (!points.length) return "";
    const w = 88;
    const h = 36;
    const pad = 2;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const coords = points.map((value, index) => {
      const x = pad + (index / Math.max(points.length - 1, 1)) * (w - pad * 2);
      const y = h - pad - ((value - min) / range) * (h - pad * 2);
      return { x, y };
    });
    return coords
      .map(
        (point, index) =>
          `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
      )
      .join(" ");
  }, [points]);

  return (
    <Box
      component="svg"
      viewBox="0 0 88 36"
      sx={{ width: 88, height: 36, display: "block", flexShrink: 0 }}
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
};

const formatValue = (value: number, format: WidgetConfig["valueFormat"]) => {
  if (format === "percent") {
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(value)}%`;
  }
  if (format === "decimal") {
    return new Intl.NumberFormat("en", {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 0,
  }).format(value);
};

const formatChangePercent = (change: number) =>
  `${new Intl.NumberFormat("en", {
    maximumFractionDigits: 0,
  }).format(Math.abs(change))}%`;

export default function CustomerStatsWidgets(props: CustomerStatsWidgetsProps) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<Record<string, any>>(emptyData());
  const [loading, setLoading] = useState(true);

  const businessId =
    props.globalFilters.businessId?.value ??
    props.globalFilters.businessId ??
    null;

  const { refetch: apiRefetch } = useGetCustomerSummaryService({
    stationId: props.globalFilters.stationId?.value || null,
    range: props.globalFilters.range?.value,
    allStation: props.globalFilters.allStation,
    ...(businessId ? { businessId: Number(businessId) } : {}),
  });

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetCustomerBreakdownResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || emptyData());
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied) props.setGlobalFilterApplied(false);
      });
  };

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded) {
      firstRender.current = false;
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stationsLoaded]);

  useEffect(() => {
    if (props.globalFilterApplied) {
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  return (
    <Grid container spacing={1.5}>
      {WIDGETS.map((widget) => {
        const stat = resolveStat(
          data,
          widget.valueKeys,
          widget.changeKeys,
          widget.sparklineKeys,
        );
        const change = stat.change;
        const isPositive = (change ?? 0) >= 0;
        const changeColor = isPositive ? "#16A34A" : "#DC2626";
        const accent = widget.accent;
        const Icon = widget.Icon;
        const sparklinePoints =
          stat.sparkline && stat.sparkline.length > 1
            ? stat.sparkline
            : buildFallbackSparkline(widget.key, change);

        return (
          <Grid item xs={12} sm={6} md={4} lg={2} key={widget.key}>
            <Box
              sx={{
                height: "100%",
                minHeight: 132,
                borderRadius: "16px",
                position: "relative",
                overflow: "hidden",
                // backgroundColor: widget.bg,
                backgroundColor: "#FFFFFF",
                border: `1px solid ${accent}22`,
                boxShadow: "none",
                px: 1.75,
                py: 1.5,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {loading ? (
                <Box>
                  <Skeleton
                    animation="pulse"
                    sx={{ backgroundColor: `${accent}22`, mb: 1 }}
                  />
                  <Skeleton
                    animation="pulse"
                    height={36}
                    sx={{ backgroundColor: `${accent}22`, mb: 1 }}
                  />
                  <Skeleton
                    animation="pulse"
                    width="55%"
                    sx={{ backgroundColor: `${accent}22` }}
                  />
                </Box>
              ) : (
                <>
                  {/* Icon + title */}
                  <Box className="flex items-center gap-1.5" sx={{ mb: 1.25 }}>
                    <Box
                      sx={{
                        ...dashboardWidgetIconSx,
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#FFFFFF",
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 17,
                          color: props.themes?.primary || "#2563EB",
                        }}
                      />
                    </Box>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1F2937",
                        lineHeight: 1.25,
                      }}
                    >
                      {t(widget.labelKey)}
                    </Typography>
                  </Box>

                  {/* Value + sparkline row */}
                  <Box
                    className="flex items-end justify-between gap-1"
                    sx={{ mt: "auto" }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Tooltip
                        followCursor
                        title={formatValue(stat.value, widget.valueFormat)}
                        enterDelay={0}
                      >
                        <Typography
                          className="font-Saira truncate"
                          sx={{
                            fontSize: 33,
                            fontWeight: 800,
                            color: "#111827",
                            lineHeight: 1,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {formatValue(stat.value, widget.valueFormat)}
                        </Typography>
                      </Tooltip>

                      {change != null ? (
                        <Box
                          className="flex items-center gap-0.5 flex-wrap"
                          sx={{ mt: 0.75 }}
                        >
                          {isPositive ? (
                            <ArrowUpwardIcon
                              sx={{ fontSize: 13, color: changeColor }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              sx={{ fontSize: 13, color: changeColor }}
                            />
                          )}
                          <Typography
                            className="font-Saira"
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: changeColor,
                              lineHeight: 1.2,
                            }}
                          >
                            {formatChangePercent(change)}
                          </Typography>
                          <Typography
                            className="font-Saira"
                            sx={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: "#9CA3AF",
                              lineHeight: 1.2,
                            }}
                          >
                            {t("vsLastWeek")}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ height: 18, mt: 0.75 }} />
                      )}
                    </Box>

                    {/* <Sparkline points={sparklinePoints} color={accent} /> */}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
